import { useRef, useState, type FormEvent } from 'react';
import { api, errorMessage } from '../../shared/api/client';
import type { AutomationKind, AutomationRule, AutomationRun } from '../../shared/api/types';
import { Alert, Button, Card, ErrorState, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { useMe } from '../session/SessionProvider';

const RULES: Record<AutomationKind, { title: string; description: string }> = {
  day_reminder: {
    title: 'Aviso los días de culto o clase',
    description:
      'A toda la iglesia: «No faltes hoy. Empezamos a las 10:00 am con la clase y luego el culto a las 11:30 am. La Palabra será: … En cafetería habrá: …». Toma el tema y quién predica del programa del culto y el menú de Cafetería; lo que no esté escrito se omite.',
  },
  service_reminder: { title: 'Recordar a quien sirve', description: 'Avisa a cada persona que aceptó servir en un culto, unas horas antes.' },
  reading_reminder: { title: 'Recordar la lectura del día', description: 'Avisa a quien sigue un plan de lectura y aún no marca la lectura de hoy.' },
};

const when = (iso: string) => new Date(iso).toLocaleString('es', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

/** Motor 20: reglas que la iglesia activa. Los avisos llegan por Notificaciones. Vive en «Configuración de la iglesia». */
export function AutomationSection({ churchId }: { churchId: string }) {
  const me = useMe();
  const data = useAsync((signal) => api.get<{ rules: AutomationRule[]; runs: AutomationRun[] }>(`/automation/churches/${churchId}/rules`, signal), [churchId]);
  // Al recargar se conserva lo último: así no se pierde el mensaje de la regla que se guardó.
  const last = useRef<{ rules: AutomationRule[]; runs: AutomationRun[] } | null>(null);
  if (data.status === 'ready') last.current = data.data;
  const view = data.status === 'ready' ? data.data : last.current;

  return (
    <>
      <p className="muted">Recordatorios que KEORA envía solo, a la bandeja de cada persona. Nada se repite: cada aviso sale una vez.</p>
      {data.status === 'loading' && !view ? <Loading /> : null}
      {data.status === 'error' ? <ErrorState error={data.error} onRetry={data.reload} /> : null}
      {view ? (
        <>
          {view.rules.map((rule) => (
            <RuleCard key={rule.kind} churchId={churchId} rule={rule} canManage={me.can('automation.manage')} onChange={data.reload} />
          ))}
          <Card title="Lo que ha hecho">
            {view.runs.length === 0 ? (
              <p className="muted">Aún no ha enviado recordatorios.</p>
            ) : (
              <ul className="list">
                {view.runs.map((r) => (
                  <li key={r.id} className="list__item">
                    <span>
                      <strong>{RULES[r.kind].title}</strong>{' '}
                      <span className="muted small">
                        {when(r.startedAt)} · {r.trigger === 'manual' ? 'a mano' : 'programado'}
                      </span>
                    </span>
                    <span className={`small${r.outcome === 'failed' ? ' text-error' : ''}`}>
                      {r.outcome === 'failed' ? `Falló: ${r.error ?? 'error'}` : r.notified === 1 ? '1 aviso' : `${r.notified} avisos`}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </>
      ) : null}
    </>
  );
}

function RuleCard({ churchId, rule, canManage, onChange }: { churchId: string; rule: AutomationRule; canManage: boolean; onChange: () => void }) {
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);
  const info = RULES[rule.kind];
  const base = `/automation/churches/${churchId}/rules/${rule.kind}`;
  const fieldId = `${rule.kind}-value`;

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const value = Number(form.get('value'));
    const config = rule.kind === 'service_reminder' ? { hoursBefore: value } : { hour: value };
    setBusy('save');
    setMessage(null);
    try {
      await api.put(base, { enabled: form.get('enabled') === 'on', config });
      setMessage({ tone: 'success', text: 'Guardado.' });
      onChange();
    } catch (e) {
      setMessage({ tone: 'error', text: errorMessage(e) });
    } finally {
      setBusy(null);
    }
  }

  async function runNow() {
    if (!window.confirm(`¿Enviar ahora «${info.title}»? Se avisará en este momento a quienes les toque.`)) return;
    setBusy('run');
    setMessage(null);
    try {
      const { notified } = await api.post<{ notified: number }>(`${base}/run`);
      setMessage({ tone: 'success', text: notified === 0 ? 'No había nadie pendiente de aviso.' : notified === 1 ? 'Se envió 1 aviso.' : `Se enviaron ${notified} avisos.` });
      onChange();
    } catch (e) {
      setMessage({ tone: 'error', text: errorMessage(e) });
    } finally {
      setBusy(null);
    }
  }

  return (
    <Card title={info.title} action={<span className={`badge${rule.enabled ? ' badge--active' : ''}`}>{rule.enabled ? 'Activa' : 'Apagada'}</span>}>
      <p className="muted small">{info.description}</p>
      <form className="form" onSubmit={save}>
        <label className="choice">
          <input type="checkbox" name="enabled" defaultChecked={rule.enabled} disabled={!canManage} />
          <span>Activar</span>
        </label>
        <div className="field">
          <label htmlFor={fieldId}>{rule.kind === 'service_reminder' ? 'Horas antes del culto' : rule.kind === 'day_reminder' ? 'A qué hora sale (hora de la iglesia)' : 'Desde qué hora (hora de la iglesia)'}</label>
          {rule.kind === 'service_reminder' ? (
            <input id={fieldId} name="value" type="number" min={1} max={72} inputMode="numeric" defaultValue={rule.config.hoursBefore} disabled={!canManage} />
          ) : (
            <select id={fieldId} name="value" defaultValue={rule.config.hour} disabled={!canManage}>
              {Array.from({ length: 24 }, (_, h) => (
                <option key={h} value={h}>
                  {String(h).padStart(2, '0')}:00
                </option>
              ))}
            </select>
          )}
        </div>
        {message ? <Alert tone={message.tone}>{message.text}</Alert> : null}
        {canManage ? (
          <div className="actions actions--wrap">
            <Button type="submit" busy={busy === 'save'}>
              Guardar
            </Button>
            <Button type="button" variant="secondary" busy={busy === 'run'} onClick={runNow}>
              Enviar ahora
            </Button>
          </div>
        ) : null}
      </form>
    </Card>
  );
}
